// src/modules/marketplace/components/realization/realizationFilters.tsx

import { FC, useEffect, useRef, useState } from "react"
import { Grid } from "@mui/material"
import {
  AutocompleteElement,
  DatePickerElement,
  FormContainer,
  useForm,
} from "react-hook-form-mui"
import equal from "fast-deep-equal"
import { useAppDispatch } from "@/modules/app/hooks"
import { ListboxComponent } from "@core/components"
import { useUrlFilters } from "@core/hooks"
import { setRealizationFilters, useRealizationsFiltersQuery } from "../../store"
import { Realization } from "../../interfaces"
import { format } from "date-fns"
import { cleanFilters, parseDateParam } from "./filterUrl"
import {
  getInitialRealizationFilters,
  REALIZATION_FILTER_SCHEMA,
} from "./initialFilters"

interface IFormData {
  categories: Realization.Category[]
  products: Realization.Product[]
  statuses: Realization.IdName[]
  couriers: Realization.IdName[]
  cities: Realization.IdName[]
  batches: Realization.BatchRef[]
  start: Date | null
  end: Date | null
}

interface IProps {
  onInitialized: () => void
}

const toQueryParams = (
  data: Partial<IFormData>,
): Realization.RealizationQueryParams => ({
  categories: data.categories?.map((c) => c.id),
  products: data.products?.map((p) => p.id),
  statuses: data.statuses?.map((s) => s.name),
  couriers: data.couriers?.map((c) => c.id),
  cities: data.cities?.map((c) => c.id),
  batches: data.batches?.map((b) => b.id),
  start: data.start ? format(data.start, "yyyy-MM-dd") : undefined,
  end: data.end ? format(data.end, "yyyy-MM-dd") : undefined,
})

export const RealizationFilters: FC<IProps> = ({ onInitialized }) => {
  const dispatch = useAppDispatch()
  const { data: filters, isLoading } = useRealizationsFiltersQuery({})
  const { read, write } = useUrlFilters(REALIZATION_FILTER_SCHEMA)
  const [initialFilters] = useState(() => getInitialRealizationFilters(read()))
  const activeFiltersRef = useRef(initialFilters)
  const didInitialize = useRef(false)
  const didRestore = useRef(false)

  const formContext = useForm<IFormData>({
    defaultValues: {
      categories: [],
      products: [],
      statuses: [],
      couriers: [],
      cities: [],
      batches: [],
      start: parseDateParam(initialFilters.start, null),
      end: parseDateParam(initialFilters.end, null),
    },
  })

  useEffect(() => {
    if (didInitialize.current) return
    didInitialize.current = true
    dispatch(setRealizationFilters(initialFilters))
    onInitialized()
  }, [dispatch, initialFilters, onInitialized])

  useEffect(() => {
    const subscription = formContext.watch((_values, { name }) => {
      if (!name) return
      const key = name.split(".")[0] as keyof IFormData
      if (!(key in REALIZATION_FILTER_SCHEMA)) return

      const serialized = toQueryParams(formContext.getValues())
      // Only merge the edited field: URL selections may still lack options.
      const next = cleanFilters({
        ...activeFiltersRef.current,
        [key]: serialized[key],
      })
      if (equal(next, activeFiltersRef.current)) return
      activeFiltersRef.current = next
      write(next)
      dispatch(setRealizationFilters(next))
    })
    return () => subscription.unsubscribe()
  }, [dispatch, formContext, write])

  // Hydrate selections once options arrive without changing active primitives
  // or overwriting dates the user may already have edited.
  useEffect(() => {
    if (didRestore.current || isLoading || !filters) return
    didRestore.current = true
    const active = activeFiltersRef.current
    formContext.reset({
      ...formContext.getValues(),
      categories: filters.categories.filter((c) =>
        active.categories?.includes(c.id),
      ),
      products: filters.products.filter((p) => active.products?.includes(p.id)),
      statuses: filters.statuses.filter((s) =>
        active.statuses?.includes(s.name),
      ),
      couriers: filters.couriers.filter((c) => active.couriers?.includes(c.id)),
      cities: filters.cities.filter((c) => active.cities?.includes(c.id)),
      batches: filters.batches.filter((b) => active.batches?.includes(b.id)),
    })
  }, [filters, isLoading, formContext])

  const autocompleteShared = {
    fullWidth: true,
    ListboxComponent: ListboxComponent,
  }

  return (
    <FormContainer formContext={formContext}>
      <Grid container spacing={1} alignItems="center" my={1}>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <AutocompleteElement
            name="categories"
            label="Категория"
            loading={isLoading}
            multiple
            showCheckbox
            options={filters?.categories ?? []}
            autocompleteProps={{
              ...autocompleteShared,
              getOptionLabel: (o: Realization.Category) => o.name,
              isOptionEqualToValue: (o, v) => o.id === v.id,
              renderOption: (props, option, state) =>
                [props, option.name, state] as React.ReactNode,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <AutocompleteElement
            name="products"
            label="Продукт"
            loading={isLoading}
            multiple
            showCheckbox
            options={filters?.products ?? []}
            autocompleteProps={{
              ...autocompleteShared,
              getOptionLabel: (o: Realization.Product) => o.name,
              isOptionEqualToValue: (o, v) => o.id === v.id,
              renderOption: (props, option, state) =>
                [props, option.name, state] as React.ReactNode,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <AutocompleteElement
            name="statuses"
            label="Статус"
            loading={isLoading}
            multiple
            showCheckbox
            options={filters?.statuses ?? []}
            autocompleteProps={{
              ...autocompleteShared,
              getOptionLabel: (o: Realization.IdName) => o.name,
              isOptionEqualToValue: (o, v) => o.name === v.name,
              renderOption: (props, option, state) =>
                [props, option.name, state] as React.ReactNode,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <AutocompleteElement
            name="couriers"
            label="Курьер"
            loading={isLoading}
            multiple
            showCheckbox
            options={filters?.couriers ?? []}
            autocompleteProps={{
              ...autocompleteShared,
              getOptionLabel: (o: Realization.IdName) => o.name,
              isOptionEqualToValue: (o, v) => o.id === v.id,
              renderOption: (props, option, state) =>
                [props, option.name, state] as React.ReactNode,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <AutocompleteElement
            name="cities"
            label="Город"
            loading={isLoading}
            multiple
            showCheckbox
            options={filters?.cities ?? []}
            autocompleteProps={{
              ...autocompleteShared,
              getOptionLabel: (o: Realization.IdName) => o.name,
              isOptionEqualToValue: (o, v) => o.id === v.id,
              renderOption: (props, option, state) =>
                [props, option.name, state] as React.ReactNode,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <AutocompleteElement
            name="batches"
            label="Партия"
            loading={isLoading}
            multiple
            showCheckbox
            options={filters?.batches ?? []}
            autocompleteProps={{
              ...autocompleteShared,
              getOptionLabel: (o: Realization.BatchRef) => o.number,
              isOptionEqualToValue: (o, v) => o.id === v.id,
              renderOption: (props, option, state) =>
                [props, option.number, state] as React.ReactNode,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <DatePickerElement
            name="start"
            label="Период с"
            inputProps={{ fullWidth: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} xl={2}>
          <DatePickerElement
            name="end"
            label="По"
            inputProps={{ fullWidth: true }}
          />
        </Grid>
      </Grid>
    </FormContainer>
  )
}
